import {
  DatePicker as NextUIDatePicker,
  DatePickerProps as NextUIDatePickerProps,
} from "@heroui/react";
import { CalendarDate, CalendarDateTime } from "@internationalized/date";
import { CalendarIcon } from "lucide-react";
import { convertCalendarDateToUnix, toCalendarDate } from "../../utils/date";
import {
  getCalendarClassNames,
  getDatePickerClassNames,
} from "../../utils/styles";

type Color = Extract<NextUIDatePickerProps["color"], "default">;
type LabelPlacement = Extract<
  NextUIDatePickerProps["labelPlacement"],
  "outside"
>;
type Variant = Extract<NextUIDatePickerProps["variant"], "bordered">;

export type DatePickerProps = Omit<
  NextUIDatePickerProps,
  | "color"
  | "labelPlacement"
  | "maxValue"
  | "minValue"
  | "selectorButtonPlacement"
  | "selectorIcon"
  | "value"
  | "variant"
> & {
  color?: Color;
  granularity?: string;
  labelPlacement?: LabelPlacement;
  maxValue?: number;
  minValue?: number;
  onChange?: (value: number | null) => void;
  ref?: React.Ref<HTMLDivElement>;
  value?: number | null;
  variant?: Variant;
};

export function DatePicker({ ref, ...props }: DatePickerProps) {
  const { granularity, maxValue, minValue, onChange, value, ...rest } = props;

  const handleChange = (
    calendarDate: CalendarDate | CalendarDateTime | null,
  ) => {
    if (!onChange) return;
    onChange(convertCalendarDateToUnix(calendarDate, granularity));
  };

  return (
    <NextUIDatePicker
      {...rest}
      ref={ref}
      calendarProps={{
        classNames: getCalendarClassNames(),
        visibleMonths: 1,
        weekdayStyle: "short",
      }}
      color="default"
      data-testid={props.name}
      dateInputClassNames={getDatePickerClassNames(props.size)}
      labelPlacement="outside"
      maxValue={toCalendarDate(maxValue, granularity)}
      minValue={toCalendarDate(minValue, granularity)}
      selectorButtonPlacement="end"
      selectorButtonProps={{ color: "primary" }}
      selectorIcon={<CalendarIcon size={16} />}
      value={
        toCalendarDate(value, granularity) as NextUIDatePickerProps["value"]
      }
      variant="bordered"
      onChange={handleChange}
    />
  );
}

DatePicker.displayName = "RecareUI.DatePicker";
